import { Box, Button, LinearProgress, Modal, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { EikenMaterialResource } from '../../../../resources';
import { UserRole } from '../../../../resources/BaseResource';

const LOADING_TEXTS = [
  'Generating Test Title.',
  'Generating Test Title..',
  'Generating Test Title...',
  'Generating Question 1.',
  'Generating Question 1..',
  'Generating Question 1...',
  'Generating Question 2.',
  'Generating Question 2..',
  'Generating Question 2...',
  'Generating Question 3.',
  'Generating Question 3..',
  'Generating Question 3...',
  'Generating Question 4.',
  'Generating Question 4..',
  'Generating Question 4...',
  'Generating Pronunciation Question.',
  'Generating Pronunciation Question..',
  'Generating Pronunciation Question...',
  'Generating Image Prompt.',
  'Generating Image Prompt..',
  'Generating Image Prompt...',
  'Finalizing.',
  'Finalizing..',
  'Finalizing...',
];

type Props = {
  open: boolean;
  onClose: () => void;
  count: number;
};

export function GenerateMaterialConfimationModal({ open, onClose, count }: Props) {
  const { user } = useAuth();
  const isAdmin = user?.data.role === UserRole.Admin;
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const navigate = useNavigate();

  async function handleGenerateTests() {
    setActiveIndex(0);
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev === null ? 0 : Math.min(prev + 1, LOADING_TEXTS.length - 1)));
    }, 2000);

    try {
      setIsLoading(true);
      const response = await EikenMaterialResource.generateRandomMaterial(count);

      navigate(`/eiken-materials/update-generated?id=${response.ids.join(',')}`);
    } finally {
      setIsLoading(false);
      clearInterval(interval);
    }
  }

  return (
    <Modal
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
          minWidth: 700,
          maxWidth: 800,
        }}
      >
        {/* Header */}
        <Box display="flex" gap={2}>
          <Typography variant="h4" fontWeight={600} mb={1}>
            Generate Tests
          </Typography>
        </Box>
        {/* Cards */}
        <Box display="flex" flexDirection="column" gap={2} mb={3}>
          <Typography variant="body1">
            This operation will use{' '}
            <b>
              {count} {count > 1 ? 'credits' : 'credit'}
            </b>{' '}
            from your account. Do you want to continue?
          </Typography>
          {activeIndex !== null && (
            <div className="pt-2">
              <Typography variant="body1">{LOADING_TEXTS[activeIndex]}</Typography>
              <LinearProgress />
            </div>
          )}
        </Box>
        {/* Add Credits Button */}
        <div className="flex justify-end gap-2">
          <Button
            variant="outlined"
            color="primary"
            sx={{ borderRadius: 2, py: 1.2, fontWeight: 600, fontSize: 16 }}
            disabled={isLoading}
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ borderRadius: 2, py: 1.2, fontWeight: 600, fontSize: 16 }}
            onClick={() => {
              handleGenerateTests();
            }}
            disabled={isLoading}
          >
            {isLoading ? <span>Generating...</span> : <span>Yes, continue</span>}
          </Button>
        </div>
      </Box>
    </Modal>
  );
}
